import { useRef } from "react";
import { useLenis } from "lenis/react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, ArrowDown } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import { MaskLine } from "./Reveal";
import { useShowreel } from "./Showreel";

const EASE = [0.25, 1, 0.5, 1];
const isVideoFile = (u) => u && /\.(mp4|webm|mov)$/i.test(u);

export const Hero = () => {
  const { content } = useContent();
  const h = content.hero;
  const ref = useRef(null);
  const lenis = useLenis();
  const { open } = useShowreel();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el && lenis) lenis.scrollTo(el, { duration: 1.4 });
  };

  return (
    <section id="hero" ref={ref} className="relative h-screen w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        {isVideoFile(h.backgroundVideoUrl) ? (
          <video src={h.backgroundVideoUrl} className="w-full h-full object-cover" autoPlay muted loop playsInline />
        ) : (
          <motion.img src={h.backgroundImage} alt="Karams Studio" className="w-full h-full object-cover"
            initial={{ scale: 1.2 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: EASE }} />
        )}
        <div className="absolute inset-0 bg-[#050505]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/70" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28">
        {h.brandTitle && (
          <motion.div className="mb-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 1, ease: EASE }}>
            <span data-testid="hero-brand-title" className="block font-serif font-semibold leading-[0.9] tracking-tight text-[16vw] sm:text-7xl md:text-8xl lg:text-9xl"
              style={{ color: /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(h.brandTitleColor || "") ? h.brandTitleColor : "#FFFFFF" }}>
              {h.brandTitle}
            </span>
          </motion.div>
        )}
        <motion.div className="flex items-center gap-3 mb-8"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 1 }}>
          <span className="h-px w-12 bg-electric" />
          <span className="text-[11px] md:text-xs tracking-[0.35em] uppercase text-secondary2">{h.eyebrow}</span>
        </motion.div>

        <h1 className="font-serif font-medium text-white leading-[0.95] tracking-tight text-[10vw] sm:text-5xl md:text-6xl lg:text-7xl max-w-6xl">
          <MaskLine delay={0.4}>{h.titleLine1}</MaskLine>
          <MaskLine delay={0.55}><span>{h.titleLine2} <span className="italic text-electric">{h.titleAccent}</span></span></MaskLine>
        </h1>

        <motion.p className="mt-8 max-w-2xl text-sm md:text-base leading-relaxed text-secondary2 font-light"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 1 }}>
          {h.subtitle}
        </motion.p>

        <motion.div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 1 }}>
          <button data-testid="hero-showreel-btn" data-track="Showreel" onClick={() => open(h.showreelUrl, "Showreel", "Karams Studio")}
            className="group inline-flex items-center gap-3 px-8 py-4 bg-surface text-white border border-white/10 rounded-full text-[13px] tracking-[0.12em] uppercase font-medium hover:border-electric hover:text-electric hover:glow-electric transition-[color,border-color,box-shadow] duration-400">
            <Play size={16} className="fill-current" />
            {h.ctaPrimary}
          </button>
          <button data-testid="hero-quote-btn" data-track="Nous rejoindre (hero)" onClick={() => go("contact")}
            className="inline-flex items-center gap-3 px-8 py-4 border border-white/20 text-white rounded-full text-[13px] tracking-[0.12em] uppercase hover:border-electric hover:text-electric transition-colors duration-400">
            {h.ctaSecondary}
          </button>
        </motion.div>
      </motion.div>

      <motion.div className="absolute bottom-8 right-6 md:right-12 z-10 flex items-center gap-2 text-white/40"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }}>
        <span className="text-[10px] tracking-[0.3em] uppercase">Défiler</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
};
